import { useEffect, useState } from 'react'
import { Popover, Button, Stack, Select, TextInput, ActionIcon, Group } from '@mantine/core'
import { Operator, Filter } from '@medplum/core'
import { IconFilter, IconPlus, IconSelector, IconTrash, IconX } from '@tabler/icons-react'

// New-design control styling shared by the filter-row selects and value input.
const CONTROL_CLASSNAMES = { input: '!rounded-md !border-[#D8DBE0] !text-[#33383F]' }
const SELECT_CHEVRON = <IconSelector size={15} className="text-[#9AA0A8]" />

type OperatorOption = { label: string; value: Operator }

const LocalOperator = {
  EQUALS: 'eq',
  NOT_EQUALS: 'ne',
  GREATER_THAN: 'gt',
  LESS_THAN: 'lt',
  GREATER_THAN_OR_EQUALS: 'ge',
  LESS_THAN_OR_EQUALS: 'le',
  STARTS_AFTER: 'sa',
  ENDS_BEFORE: 'eb',
  CONTAINS: 'contains',
  STARTS_WITH: 'sw',
  NOT: 'not',
  ABOVE: 'above',
  BELOW: 'below',
  IN: 'in',
  NOT_IN: 'not-in',
}

const operatorOptions: OperatorOption[] = Object.entries(LocalOperator).map(([key, value]) => ({
  label: key.replace(/_/g, ' ').toLowerCase(),
  value: value as Operator,
}))

type Props = {
  fields: { label: string; value: string }[]
  value: Filter[]
  onChange: (filters: Filter[]) => void
  // Controlled open state so the parent can keep only one toolbar popup open at a time.
  opened: boolean
  onOpenChange: (opened: boolean) => void
}

let timeOut: NodeJS.Timeout

export function FilterMenu({ fields, value, onChange, opened, onOpenChange }: Props) {
  const [filters, setFilters] = useState<Filter[]>(value)

  useEffect(() => {
    setFilters(value)
  }, [value])

  const handleFilterChange = (idx: number, patch: Partial<Filter>) => {
    setFilters((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }

      const isFilterSet = next.reduce(
        (acc, curr) => acc && !!curr.code && !!curr.operator && !!curr.value,
        true,
      )

      if (isFilterSet) {
        if (patch.value) {
          if (timeOut) {
            clearTimeout(timeOut)
          }
          timeOut = setTimeout(() => onChange(next), 400)
        } else {
          onChange(next)
        }
      }
      return next
    })
  }

  const handleAdd = () => {
    setFilters((prev) => {
      return [...prev, { code: '', operator: Operator.EQUALS, value: '' }]
    })
  }

  const handleRemove = (idx: number) => {
    setFilters((prev) => {
      const next = prev.filter((_, i) => i !== idx)
      onChange(next)
      return next
    })
  }

  return (
    <Popover
      opened={opened}
      closeOnClickOutside={false}
      trapFocus={false}
      withinPortal={false}
      onChange={onOpenChange}
      width={440}
      shadow="md"
      radius="lg"
      position="bottom-start"
    >
      <Popover.Target>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#D8DBE0] bg-white px-2.5 py-[3px] !text-[16px] !font-semibold text-[#33383F] transition hover:bg-[#F4F6F8]"
          onClick={() => onOpenChange(!opened)}
        >
          <IconFilter size={16} />
          Filters
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={8} p={8} justify="start">
          {filters.map((filter, idx) => (
            <Group key={idx} align="center" gap={8} wrap="nowrap">
              <Select
                placeholder="Field…"
                data={fields}
                value={filter.code}
                onChange={(code) => handleFilterChange(idx, { code: code ?? '' })}
                w={128}
                radius="md"
                classNames={CONTROL_CLASSNAMES}
                rightSection={SELECT_CHEVRON}
                searchable
                allowDeselect={false}
              />
              <Select
                placeholder="Operator"
                data={operatorOptions}
                value={filter.operator}
                onChange={(op) => handleFilterChange(idx, { operator: op as Operator })}
                w={120}
                radius="md"
                classNames={CONTROL_CLASSNAMES}
                rightSection={SELECT_CHEVRON}
                searchable
                allowDeselect={false}
              />
              <TextInput
                placeholder="Value…"
                value={filter.value}
                onChange={(e) => handleFilterChange(idx, { value: e.target.value })}
                w={110}
                radius="md"
                classNames={CONTROL_CLASSNAMES}
              />
              <ActionIcon
                variant="default"
                radius="md"
                size={36}
                className="!border-[#D8DBE0] !text-[#6B7280] hover:!bg-[#F4F6F8]"
                onClick={() => handleRemove(idx)}
                aria-label="Delete"
              >
                <IconTrash size={17} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
        <div className="flex items-center justify-between px-1 pt-1">
          <Button
            variant="transparent"
            className="!px-1 !font-bold !text-[#E5533D]"
            leftSection={<IconPlus size={18} stroke={2.5} />}
            onClick={handleAdd}
            size="sm"
          >
            Add Filter
          </Button>
          <Button
            variant="light"
            radius="md"
            className="!bg-[#EEF2F8] !font-semibold !text-[#5B6472] hover:!bg-[#E3E9F2]"
            leftSection={<IconX size={16} stroke={2.5} />}
            onClick={() => onOpenChange(false)}
            size="sm"
          >
            Close
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}
